/**
 * The site-wide copy for the company story band.
 *
 * Here rather than in `pages/about/content.ts` for the reason
 * `content/deliveryProof.ts` gives about its own band: this is copy that five
 * pages besides /about now render, and page-local copy another page imports is
 * page-local copy in the wrong file.
 *
 * Nothing here is a new claim. Everything the band actually asserts — the two
 * years, the legal name, the office, what we do for a customer — is read from
 * `config/company.ts` by `useCompanyStory` at render time. What lives here is
 * only the framing: the heading each host page gets when it does not write its
 * own, and the link every copy of the band shares.
 *
 * One claim, two lengths. Both variants below say the same thing — we are a
 * Kampala company that has been importing since 2018 — because a band that
 * promised something slightly different on each page would be six slightly
 * different promises to keep. What changes between them is how much room the
 * host page can spare, not what is being said.
 */

export const ABOUT_PATH = '/about';

/**
 * The compact band's call to action, identical everywhere so that six copies
 * of the band read as one recognisable place rather than six coincidences.
 */
export const companyStoryCta = { label: 'More about Kabex', to: ABOUT_PATH } as const;

/**
 * The full band's call to action.
 *
 * It differs from the compact one because the destination has to: the full
 * band *is* the About page's opening, so "More about Kabex" there would be a
 * link to the page the reader is already on. The office is the next thing a
 * reader of that band wants.
 */
export const companyStoryVisitCta = { label: 'Visit our office', to: '/contact' } as const;

/**
 * The full band, as /about opens with it: a sticky question beside the
 * narrative that answers it.
 */
export const companyStoryBand = {
  eyebrow: 'Our story',
  title: 'A Kampala company buying from the world',
  lede: 'We have been importing cars for customers in Uganda since 2018, from dealers in Japan, China, the UK and beyond.',
} as const;

/**
 * The compact band, for a page borrowing it.
 *
 * The eyebrow says "About Kabex Motors" rather than "Our story" on purpose,
 * and it is the whole reason this band exists: the About page sits inside a
 * "Company" dropdown where visitors were not finding it, so the band that
 * stands in for it elsewhere has to name itself in the words someone looking
 * for it would use.
 */
export const companyStoryTeaser = {
  eyebrow: 'About Kabex Motors',
  title: 'The company behind your import',
  lede: 'Who we are, how long we have been doing this, and where to find us.',
} as const;
